/*
 * Nexus Elite Solutions - 2nd October 2026, revised 5th October 2026
 * Cart drawer: audit point 2 - suggest additional items that go with what is
 * already in the cart.
 *
 * WHERE THE SUGGESTIONS COME FROM
 * Shopify's own recommendations endpoint with intent=complementary, which reads
 * the "Pairs well with" data set on the products themselves. On this store that
 * data was built from GiG's own bundle products, so a shopper is shown the
 * pairings the merchant actually sells together - never a guess made here.
 * If nothing in the cart has pairings set, the endpoint returns empty lists and
 * the whole block stays hidden rather than falling back to something random.
 *
 * WHY SOME CARDS ADD AND OTHERS LINK
 * A piece with one variant can be added straight from the cart. A piece with
 * sizes cannot - silently adding a default size is how shoppers end up with a
 * ring that does not fit. Those cards link to the product instead.
 *
 * The block is rendered outside .cart--body, so the theme's cart update does
 * not destroy it; this script refreshes it on theme:cart:updated instead.
 *
 * ---------------------------------------------------------------------------
 * 5th October 2026 - the client asked for pairings that complement the item in
 * the cart. Three things were wrong with doing that off cart.items[0] alone:
 *
 *   1. ONLY THE FIRST LINE WAS CONSULTED. A cart of three pieces was pitched
 *      against the first one, and the other two were ignored.
 *   2. ONE UNSET PRODUCT HID EVERYTHING. If that first piece had no pairings
 *      configured, the block hid itself even when the rest of the cart did.
 *   3. PAIRINGS ALREADY IN THE CART WERE STILL OFFERED. Nothing filtered them,
 *      so a shopper could be invited to add a piece they had already added.
 *
 * So cart.liquid now emits every product id in the cart, in cart order, and
 * this asks each one for its pairings. The answers are INTERLEAVED rather than
 * concatenated - one from the first line, one from the second, and so on -
 * because taking them in order would let a single product with a long pairing
 * list fill the block and crowd out the rest of the cart entirely.
 *
 * The id list doubles as the exclusion set, which fixes (3), and a few more
 * candidates than needed are requested per product so that filtering what is
 * already in the cart does not empty the block.
 */
(function () {
  'use strict';

  /* Shown at most; FETCH is per product, deliberately higher so that removing
     duplicates and items already in the cart still leaves something to show. */
  var LIMIT = 3;
  var FETCH = 4;
  var loaded = {};

  function CartClass() {
    return window.customElements && window.customElements.get('cart-element');
  }

  function recommendationsUrl(productId) {
    var r = (window.Shopify && window.Shopify.routes) || {};
    var base = r.product_recommendations || '/recommendations/products';
    return base + '.json?product_id=' + productId + '&limit=' + FETCH + '&intent=complementary';
  }

  function cartUrl() {
    var r = (window.Shopify && window.Shopify.routes) || {};
    return (r.cart || '/cart') + '.js';
  }

  /* The block lives OUTSIDE .cart--body, which is the only reason it survives a
     cart update - but it also means the theme never re-renders it, so the
     data-product-ids attribute Liquid wrote is only correct on first paint.
     Reading the live cart instead is what keeps the suggestions and the
     exclusion set honest after an add or a removal. The attribute is still the
     fallback, so the block works on first paint and if the request fails. */
  function currentIds(block) {
    return fetch(cartUrl(), { headers: { Accept: 'application/json' } })
      .then(function (res) { return res.ok ? res.json() : null; })
      .then(function (cart) {
        if (!cart || !cart.items) return null;
        return cart.items.map(function (item) { return item.product_id; }).join(',');
      })
      .catch(function () { return null; })
      .then(function (raw) {
        /* Both paths go through parseIds, so a product sitting on two cart lines
           is counted once either way. */
        return parseIds(raw === null ? block.getAttribute('data-product-ids') : raw);
      });
  }

  function money(cents) {
    try {
      return (cents / 100).toLocaleString(undefined, {
        style: 'currency',
        currency: (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'USD'
      });
    } catch (error) {
      return (cents / 100).toFixed(2);
    }
  }

  function thumb(product) {
    var src = product.featured_image || (product.images && product.images[0]);
    if (!src) return '';
    var sized = src.indexOf('?') === -1 ? src + '?width=160' : src + '&width=160';
    var image = document.createElement('img');
    image.src = sized;
    image.alt = '';
    image.loading = 'lazy';
    image.width = 80;
    image.height = 80;
    return image;
  }

  function addable(product) {
    return product.available && product.variants && product.variants.length === 1 && product.variants[0].available;
  }

  function card(product) {
    var root = document.createElement('div');
    root.className = 'cart--cross-sell-item';

    var link = document.createElement('a');
    link.className = 'cart--cross-sell-image';
    link.href = product.url;
    var image = thumb(product);
    if (image) link.appendChild(image);
    root.appendChild(link);

    var info = document.createElement('div');
    info.className = 'cart--cross-sell-info';

    var title = document.createElement('a');
    title.className = 'cart--cross-sell-title';
    title.href = product.url;
    title.setAttribute('data-item', 'nav-text');
    title.textContent = product.title;
    info.appendChild(title);

    var price = document.createElement('div');
    price.className = 'cart--cross-sell-price';
    price.setAttribute('data-item', 'small-text');
    price.textContent = money(product.price);
    info.appendChild(price);

    if (addable(product)) {
      var button = document.createElement('button');
      button.type = 'button';
      button.className = 'cart--cross-sell-add';
      button.setAttribute('data-item', 'block-link');
      button.setAttribute('data-variant-id', product.variants[0].id);
      button.textContent = 'Add';
      info.appendChild(button);
    } else {
      var choose = document.createElement('a');
      choose.className = 'cart--cross-sell-add';
      choose.href = product.url;
      choose.setAttribute('data-item', 'block-link');
      choose.textContent = 'Choose options';
      info.appendChild(choose);
    }

    root.appendChild(info);
    return root;
  }

  /* The attribute carries a trailing comma and may repeat a product that sits on
     two cart lines, so empties and duplicates are both dropped here. */
  function parseIds(raw) {
    var out = [];
    var seen = {};
    (raw || '').split(',').forEach(function (part) {
      var id = part.replace(/\s+/g, '');
      if (!id || seen[id]) return;
      seen[id] = true;
      out.push(id);
    });
    return out;
  }

  /* A failed or empty lookup resolves to [] rather than rejecting, so one
     product without pairings cannot take the whole block down with it. */
  function pairingsFor(productId) {
    return fetch(recommendationsUrl(productId), { headers: { Accept: 'application/json' } })
      .then(function (res) { return res.ok ? res.json() : null; })
      .then(function (data) { return (data && data.products) || []; })
      .catch(function () { return []; });
  }

  /* One from the first cart line, one from the second, and round again - so a
     single product with a long pairing list cannot crowd out the rest. */
  function interleave(lists, exclude) {
    var picked = [];
    var seen = {};
    var depth = 0;
    var longest = 0;

    lists.forEach(function (list) {
      if (list.length > longest) longest = list.length;
    });

    for (depth = 0; depth < longest && picked.length < LIMIT; depth++) {
      for (var i = 0; i < lists.length && picked.length < LIMIT; i++) {
        var product = lists[i][depth];
        if (!product) continue;

        var id = String(product.id);
        if (seen[id] || exclude[id]) continue;

        seen[id] = true;
        picked.push(product);
      }
    }

    return picked;
  }

  function fill(block, ids) {
    if (!ids.length) {
      block.setAttribute('aria-hidden', 'true');
      return;
    }

    var key = ids.join(',');
    if (loaded[key]) {
      render(block, loaded[key]);
      return;
    }

    var exclude = {};
    ids.forEach(function (id) { exclude[String(id)] = true; });

    Promise.all(ids.map(pairingsFor)).then(function (lists) {
      var products = interleave(lists, exclude);
      loaded[key] = products;
      render(block, products);
    });
  }

  function render(block, products) {
    var list = block.querySelector('.cart--cross-sell-items');
    if (!list) return;

    if (!products.length) {
      block.setAttribute('aria-hidden', 'true');
      return;
    }

    var fragment = document.createDocumentFragment();
    products.forEach(function (product) { fragment.appendChild(card(product)); });

    list.innerHTML = '';
    list.appendChild(fragment);
    block.setAttribute('aria-hidden', 'false');
  }

  function add(button) {
    var Cart = CartClass();
    var id = button.getAttribute('data-variant-id');
    if (!Cart || !id || button.disabled) return;

    button.disabled = true;

    Cart.addItems({
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        items: [{ id: parseInt(id, 10), quantity: 1 }],
        sections: Cart.sections
      })
    }, null).catch(function () {
      button.disabled = false;
    });
  }

  /* The cart page carries three cart views - drawer, desktop and mobile - and
     every one of them has this block, so the cart is read once and the answer is
     shared rather than fetched three times. */
  function run() {
    var blocks = document.querySelectorAll('.cart--cross-sell');
    if (!blocks.length) return;

    currentIds(blocks[0]).then(function (ids) {
      Array.prototype.forEach.call(blocks, function (block) { fill(block, ids); });
    });
  }

  function init() {
    document.addEventListener('click', function (event) {
      var button = event.target && event.target.closest
        ? event.target.closest('.cart--cross-sell-add')
        : null;
      if (button && button.tagName === 'BUTTON') {
        event.preventDefault();
        add(button);
      }
    });

    run();
    window.addEventListener('theme:cart:updated', run);
    window.addEventListener('theme:drawer:right:opened', run);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
